import { useCallback } from "react";
import type {
  UseFormReturn,
} from "react-hook-form";
import { getFormFieldErrorMsg } from "@tm/insurance/utils";
import type { ITravellerDetailsFormV2 } from "../../AddTravellerDetail/type";
import type { TravellerSecondaryFieldFlags } from "../TravellerDetailsV2.types";

interface TravellerError {
  index: number;
  errorType: string;
}

type FormFieldType = "name" | "dob" | "gender" | "passport" | "nomineeName";

/**
 * Handles traveller reordering (error rows first → selected → rest),
 * per-field validation against `secondaryContactFieldsRef` flags,
 * and scrolling to the first error traveller.
 */
export function useTravellerValidation(
  methods: Pick<
    UseFormReturn<ITravellerDetailsFormV2>,
    "getValues" | "reset" | "setError" | "trigger"
  >,
  setForceShowAllTravellers: React.Dispatch<React.SetStateAction<boolean>>,
) {
  const { getValues, reset, setError, trigger } = methods;

  const scrollToErrorTraveller = useCallback(
    (errorIndex: number, delay = 300) => {
      setTimeout(() => {
        const el = document.querySelector(
          `[data-traveller-index="${errorIndex}"]`,
        );
        el?.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
      }, delay);
    },
    [],
  );

  const filterTravellers = useCallback(
    async (selected: number[], errors: TravellerError[]) => {
      const currentData = getValues("travellerData") || [];
      let targetPosition = 0;
      const newIndices: number[] = new Array(currentData.length);
      const errorIndices = errors.map((err) => err.index);

      currentData.forEach((_t, index) => {
        if (errorIndices.includes(index)) {
          newIndices[index] = targetPosition++;
        }
      });
      currentData.forEach((_t, index) => {
        if (selected.includes(index) && !errorIndices.includes(index)) {
          newIndices[index] = targetPosition++;
        }
      });
      currentData.forEach((_t, index) => {
        if (!selected.includes(index) && !errorIndices.includes(index)) {
          newIndices[index] = targetPosition++;
        }
      });

      const newArray: typeof currentData = [];
      newIndices.forEach((indexVal, index) => {
        newArray[indexVal] = currentData[index];
      });

      reset(
        { ...getValues(), travellerData: newArray },
        { keepDefaultValues: true },
      );

      await new Promise((r) => setTimeout(r, 50));
      await trigger("travellerData");

      if (errors.length > 0) {
        setForceShowAllTravellers(true);
      }

      errors.forEach(({ errorType }, ind) => {
        setError(`travellerData.${ind}.${errorType}` as any, {
          message: getFormFieldErrorMsg(errorType as FormFieldType),
        });
      });
    },
    [getValues, reset, setError, trigger, setForceShowAllTravellers],
  );

  const collectTravellerErrors = useCallback(
    (secondaryFieldFlags: TravellerSecondaryFieldFlags) => {
      const formVals = getValues();
      const selected: number[] = [];
      const errorArr: TravellerError[] = [];
      const sc = secondaryFieldFlags;

      formVals.travellerData?.forEach((item, index) => {
        const {
          name: fullName,
          dob: dobValue,
          gender: genderType,
          passport: passportType,
          nomineeName: nomineeNameType,
          isSelected,
        } = item || {};

        if (isSelected) {
          selected.push(index);
          if (sc?.name?.isRequired && !fullName?.trim()) {
            errorArr.push({ index, errorType: "name" });
          } else if (sc?.dob?.isRequired && !dobValue) {
            errorArr.push({ index, errorType: "dob" });
          } else if (sc?.gender?.isRequired && !genderType?.trim()) {
            errorArr.push({ index, errorType: "gender" });
          } else if (sc?.passport?.isRequired && !passportType?.trim()) {
            errorArr.push({ index, errorType: "passport" });
          } else if (sc?.nomineeName?.isRequired && !nomineeNameType?.trim()) {
            errorArr.push({ index, errorType: "nomineeName" });
          }
        }
      });

      return { selected, errorArr };
    },
    [getValues],
  );

  return {
    scrollToErrorTraveller,
    filterTravellers,
    collectTravellerErrors,
  };
}
